import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AchBankFields, {
  achBankReady,
  type AchAccountType,
} from '../../Components/AchBankFields';
import {
  applyPayment,
  completePayflowSession,
  createPayflowSession,
  getPayflowSession,
  payLoanACH,
  previewPayment,
  type CreatePayflowSessionResponse,
  type PaymentPreviewResponse,
} from '../../endpoints/loans';
import { getApiErrorMessage } from '../../utils/httpClientTs';
import PayflowHostedCardFrame, {
  payflowDeclineMessage,
  type PayflowHostedMessage,
} from '../../Components/PayflowHostedCardFrame';

type PaymentMethod = 'check' | 'card' | 'bank';

type ElfLoanPaymentDialogProps = {
  open: boolean;
  onClose: () => void;
  borrowerUserId: string;
  currency?: string;
  onSuccess: () => void;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const ElfLoanPaymentDialog: React.FC<ElfLoanPaymentDialogProps> = ({
  open,
  onClose,
  borrowerUserId,
  currency = 'USD',
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('check');
  const [amount, setAmount] = useState('');
  const [postingDate, setPostingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [checkNumber, setCheckNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState<AchAccountType>('checking');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [previewPending, setPreviewPending] = useState(false);
  const [preview, setPreview] = useState<PaymentPreviewResponse | null>(null);
  const [payflowSession, setPayflowSession] =
    useState<CreatePayflowSessionResponse | null>(null);
  const [cardIntentPending, setCardIntentPending] = useState(false);
  const [cardCompleting, setCardCompleting] = useState(false);
  const [waitingConfirm, setWaitingConfirm] = useState(false);
  const finalizeInFlightRef = useRef(false);
  const cardWaitStartedRef = useRef(0);

  const resetForm = () => {
    setPaymentMethod('check');
    setAmount('');
    setPostingDate(new Date().toISOString().slice(0, 10));
    setCheckNumber('');
    setRoutingNumber('');
    setAccountNumber('');
    setAccountType('checking');
    setError(null);
    setPending(false);
    setPreviewPending(false);
    setPreview(null);
    setPayflowSession(null);
    setCardIntentPending(false);
    setCardCompleting(false);
    setWaitingConfirm(false);
    finalizeInFlightRef.current = false;
    cardWaitStartedRef.current = 0;
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setError(null);
    setPreview(null);
  }, [paymentMethod]);

  const parsedAmount = Number(amount);
  const canPay = parsedAmount > 0 && postingDate.trim() !== '';
  const previewMatchesInput =
    !!preview &&
    preview.amount === parsedAmount &&
    preview.posting_date === postingDate;

  const clearPreview = () => {
    if (payflowSession) return;
    setPreview(null);
  };

  const handleClose = () => {
    if (pending || cardIntentPending || previewPending || cardCompleting)
      return;
    onClose();
  };

  const handleReview = async () => {
    setError(null);
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (paymentMethod === 'check' && !checkNumber.trim()) {
      setError('Check or reference number is required.');
      return;
    }
    setPreviewPending(true);
    try {
      const data = await previewPayment({
        user_id: borrowerUserId,
        amount: parsedAmount,
        posting_date: postingDate,
      });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(getApiErrorMessage(err));
    } finally {
      setPreviewPending(false);
    }
  };

  const handleSubmitCheck = async () => {
    setError(null);
    const ref = checkNumber.trim();
    if (!ref) {
      setError('Check or reference number is required.');
      return;
    }
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (!previewMatchesInput) {
      setError('Review the payment preview before applying.');
      return;
    }
    setPending(true);
    try {
      await applyPayment({
        user_id: borrowerUserId,
        amount: parsedAmount,
        posting_date: postingDate,
        payment_method: 'check',
        payment_ref: ref,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const bankReady = achBankReady(routingNumber, accountNumber, accountType);

  const handleSubmitBank = async () => {
    setError(null);
    if (!canPay || !bankReady) {
      setError('Enter the amount, posting date, and a valid bank account.');
      return;
    }
    if (!previewMatchesInput) {
      setError('Review the payment preview before applying.');
      return;
    }
    setPending(true);
    try {
      await payLoanACH({
        user_id: borrowerUserId,
        amount: parsedAmount,
        posting_date: postingDate,
        routing_number: routingNumber.trim(),
        account_number: accountNumber.trim(),
        account_type: accountType,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handlePrepareCard = async () => {
    setError(null);
    setPayflowSession(null);
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (!previewMatchesInput) {
      setError('Review the payment preview before continuing to card entry.');
      return;
    }
    setCardIntentPending(true);
    try {
      const data = await createPayflowSession({
        user_id: borrowerUserId,
        purpose: 'payment',
        amount: parsedAmount,
        posting_date: postingDate,
      });
      cardWaitStartedRef.current = Date.now();
      setWaitingConfirm(false);
      setPayflowSession(data);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setCardIntentPending(false);
    }
  };

  const finalizePayflowPayment = useCallback(async () => {
    if (!payflowSession || finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setCardCompleting(true);
    setError(null);
    try {
      await completePayflowSession(payflowSession.session_id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(
        getApiErrorMessage(err) ||
          'Waiting for PayPal to confirm this charge. Do not enter the card again.'
      );
    } finally {
      setCardCompleting(false);
      finalizeInFlightRef.current = false;
    }
  }, [payflowSession, onSuccess, onClose]);

  useEffect(() => {
    if (!payflowSession) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || finalizeInFlightRef.current) return;
      if (
        cardWaitStartedRef.current &&
        Date.now() - cardWaitStartedRef.current > 45000
      ) {
        setWaitingConfirm(true);
      }
      try {
        const st = await getPayflowSession(payflowSession.session_id);
        if (stopped) return;
        if (st.status === 'completed' || st.payment_id) {
          onSuccess();
          onClose();
          return;
        }
        if (
          st.status === 'declined' ||
          st.status === 'failed' ||
          st.status === 'expired'
        ) {
          setError(payflowDeclineMessage(st.respmsg, st.result));
          setPayflowSession(null);
          return;
        }
        if (st.status === 'approved' || st.has_pnref) {
          void finalizePayflowPayment();
        }
      } catch {
        // Keep polling until silent post, inquiry, or return page lands.
      }
    };
    const id = window.setInterval(() => void tick(), 2000);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [payflowSession, finalizePayflowPayment, onSuccess, onClose]);

  const onPayflowMessage = (msg: PayflowHostedMessage) => {
    if (msg.status === 'approved') {
      void finalizePayflowPayment();
      return;
    }
    if (msg.status === 'canceled') {
      setError('Card entry was canceled.');
      setPayflowSession(null);
      return;
    }
    setError(payflowDeclineMessage(msg.respmsg, msg.result));
    setPayflowSession(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Make payment</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Payment applies across this borrower&apos;s active loans ({currency}).
          Review the allocation before applying.
        </Typography>

        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            value={paymentMethod}
            onChange={(_, value) => value && setPaymentMethod(value)}
            size="small"
            fullWidth
            disabled={!!payflowSession || cardCompleting}
          >
            <ToggleButton value="check">Check</ToggleButton>
            <ToggleButton value="card">Card</ToggleButton>
            <ToggleButton value="bank">Bank account</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Amount"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0.01, step: 0.01 }}
            value={amount}
            disabled={!!payflowSession || cardCompleting}
            onChange={(e) => {
              setAmount(e.target.value);
              clearPreview();
            }}
          />
          <TextField
            label="Posting date"
            type="date"
            size="small"
            fullWidth
            value={postingDate}
            disabled={!!payflowSession || cardCompleting}
            onChange={(e) => {
              setPostingDate(e.target.value);
              clearPreview();
            }}
            InputLabelProps={{ shrink: true }}
          />

          {paymentMethod === 'check' && (
            <TextField
              label="Check or reference number"
              size="small"
              fullWidth
              required
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
            />
          )}

          {paymentMethod === 'bank' && (
            <AchBankFields
              routingNumber={routingNumber}
              accountNumber={accountNumber}
              accountType={accountType}
              onRoutingNumber={setRoutingNumber}
              onAccountNumber={setAccountNumber}
              onAccountType={setAccountType}
              disabled={pending}
            />
          )}

          <Button
            variant="outlined"
            disabled={!canPay || previewPending || pending || !!payflowSession}
            onClick={() => void handleReview()}
          >
            {previewPending ? 'Reviewing…' : 'Review payment'}
          </Button>

          {previewMatchesInput && preview && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Payment preview
              </Typography>
              <Stack spacing={0.75}>
                <PreviewRow
                  label="Charges"
                  value={fmtMoney(preview.total_applied_to_charges)}
                />
                <PreviewRow
                  label="Interest due"
                  value={fmtMoney(preview.total_applied_to_interest)}
                />
                <PreviewRow
                  label="Principal"
                  value={fmtMoney(preview.total_applied_to_principal)}
                />
                {preview.total_accrued_deferred > 0 && (
                  <PreviewRow
                    label="Accrued (this payment)"
                    value={fmtMoney(preview.total_accrued_deferred)}
                  />
                )}
                {preview.has_payoff_billing && (
                  <PreviewRow
                    label="Accrued billed on payoff"
                    value={fmtMoney(preview.total_deferred_billed_on_payoff)}
                  />
                )}
                {preview.total_applied_to_interest_after_bill > 0 && (
                  <PreviewRow
                    label="Applied to billed accrued"
                    value={fmtMoney(
                      preview.total_applied_to_interest_after_bill
                    )}
                  />
                )}
                {preview.total_interest_waived > 0 && (
                  <PreviewRow
                    label="Interest waived (under $10)"
                    value={fmtMoney(preview.total_interest_waived)}
                  />
                )}
                {preview.unapplied_amount > 0 && (
                  <PreviewRow
                    label="Unapplied"
                    value={fmtMoney(preview.unapplied_amount)}
                  />
                )}
              </Stack>

              {preview.loans.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Per loan
                  </Typography>
                  <Stack spacing={1}>
                    {preview.loans.map((loan) => (
                      <Box key={loan.loan_id}>
                        <Typography variant="body2" fontWeight={600}>
                          {loan.loan_number
                            ? `Loan #${loan.loan_number}`
                            : `Loan ${loan.loan_id.slice(0, 8)}`}
                          {loan.would_pay_off ? ' · payoff' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Charges {fmtMoney(loan.applied_to_charges)} · Interest{' '}
                          {fmtMoney(loan.applied_to_interest)} · Principal{' '}
                          {fmtMoney(loan.applied_to_principal)}
                          {loan.deferred_billed_on_payoff > 0
                            ? ` · Bill accrued ${fmtMoney(loan.deferred_billed_on_payoff)}`
                            : ''}
                          {loan.applied_to_interest_after_bill > 0
                            ? ` · After bill ${fmtMoney(loan.applied_to_interest_after_bill)}`
                            : ''}
                          {loan.interest_waived > 0
                            ? ` · Waive ${fmtMoney(loan.interest_waived)}`
                            : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

              {preview.warnings?.map((warning) => (
                <Alert key={warning} severity="warning" sx={{ mt: 1.5 }}>
                  {warning}
                </Alert>
              ))}
            </Box>
          )}

          {paymentMethod === 'card' && (
            <>
              {!payflowSession && (
                <Button
                  variant="contained"
                  disabled={
                    !canPay || !previewMatchesInput || cardIntentPending
                  }
                  onClick={() => void handlePrepareCard()}
                >
                  {cardIntentPending ? 'Preparing…' : 'Continue to card entry'}
                </Button>
              )}
              {payflowSession && (
                <PayflowHostedCardFrame
                  iframeUrl={payflowSession.iframe_url}
                  sessionId={payflowSession.session_id}
                  onMessage={onPayflowMessage}
                />
              )}
              {cardCompleting && (
                <Alert severity="info">Applying payment…</Alert>
              )}
              {payflowSession && !cardCompleting && (
                <Alert severity="warning">
                  If PayPal showed a receipt, do not enter the card again. We
                  will post this payment as soon as PayPal confirms it.
                </Alert>
              )}
              {waitingConfirm && !cardCompleting && (
                <Alert severity="info">
                  Still waiting for PayPal confirmation. Leave this window open
                  if you can; do not start a second card payment.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={
            pending || cardIntentPending || previewPending || cardCompleting
          }
        >
          Cancel
        </Button>
        {(paymentMethod === 'check' || paymentMethod === 'bank') && (
          <Button
            variant="contained"
            disabled={
              !canPay ||
              !previewMatchesInput ||
              pending ||
              (paymentMethod === 'bank' && !bankReady)
            }
            onClick={
              paymentMethod === 'bank' ? handleSubmitBank : handleSubmitCheck
            }
          >
            {pending ? 'Applying…' : 'Apply payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const PreviewRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

export default ElfLoanPaymentDialog;
